import { useState } from 'react';
import { ApiFailure, api, deviceId, deviceLabel, tokens } from '../lib/api';
import { Package } from 'lucide-react';

interface LoginResult {
  accessToken: string;
  refreshToken: string;
  mustChangePassword: boolean;
  user: { id: string; name: string; kind: string };
}

export function Login({
  onDone,
  notice,
}: {
  onDone: (mustChangePassword: boolean) => void;
  /** 从改密页跳回来时说一句 —— 不说的话像是改密失败了 */
  notice?: string | null;
}) {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const r = await api<LoginResult>('/auth/login', {
        method: 'POST',
        noRetry: true,
        body: {
          identifier: account.trim(),
          password,
          deviceId: deviceId(),
          deviceLabel: deviceLabel(),
        },
      });
      tokens.set({ accessToken: r.accessToken, refreshToken: r.refreshToken });
      // ★ 登录接口直接就说了要不要改密,不用等下一个请求 401 才发现
      onDone(r.mustChangePassword);
    } catch (e) {
      // 后端已经把错误翻译成人话,直接显示
      setError(e instanceof ApiFailure ? e.message : '网络异常,请稍后重试');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col justify-center px-6 bg-slate-50 dark:bg-slate-900">
      {/*
        ★ 限宽居中,不然在电脑上两个框会横贯整个屏幕。
          这一端在手机上也要用(调度在路上看一眼订单),所以手机那一档
          原样不动 —— 只是不再让它把手机的尺寸一路撑到 1920。
          改密页早就是这么写的,这里照搬同一种写法。
      */}
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-10 text-center">
          <Package className="mx-auto mb-3 size-12 text-brand-600" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">管理端</h1>
        </div>

        {notice && (
          <div className="mb-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800
                          dark:bg-green-950/40 dark:text-green-300">
            {notice}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="account" className="block text-sm text-slate-600 dark:text-slate-400 mb-1.5">
              账号
            </label>
            <input
              id="account"
              type="text"
              inputMode="text"
              autoComplete="username"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder="admin"
              // 字号 18px:司机可能在阳光下、戴手套操作
              className="w-full h-14 px-4 text-lg rounded-xl border border-slate-300 dark:border-slate-700
                         bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                         focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm text-slate-600 dark:text-slate-400 mb-1.5">
              密码
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-14 px-4 text-lg rounded-xl border border-slate-300 dark:border-slate-700
                         bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                         focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {error && (
            <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900
                            px-4 py-3 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !account || !password}
            className="w-full h-14 sm:h-12 rounded-xl bg-brand-600 text-white text-lg sm:text-base font-medium
                       disabled:opacity-40 active:bg-brand-700 transition"
          >
            {busy ? '登录中…' : '登录'}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-slate-500">
          忘记密码请联系系统管理员
        </p>
      </div>
    </div>
  );
}
